import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Building2, Plus, X, Search } from 'lucide-react'
import { NewCompanyModal } from '@/pages/crm/Companies'

/**
 * CompanyPicker — typeahead for the contact ↔ company link.
 *
 * Controlled by `{ id, name }`. The picker maintains its own draft input while
 * the user is typing; the parent only sees a committed value when the user
 * picks an existing company from the dropdown or finishes "Create new" via
 * the embedded NewCompanyModal.
 *
 * Free-text path: if the user types and never picks, no commit fires —
 * `value.id` stays whatever it was last set to, and `value.name` is unchanged.
 * The clear button (×) is the only way to unset a picked company.
 *
 * Disabled mode: when `disabled` is true the picker renders as a read-only
 * label (used when the parent form has a locked `initialCompany` context,
 * e.g. adding a contact from the Company Detail Contacts tab).
 */

export interface CompanyValue {
  id: string | null
  name: string
}

interface Props {
  workspaceId: string
  value: CompanyValue
  onChange: (next: CompanyValue) => void
  disabled?: boolean
  placeholder?: string
}

interface CompanyRow {
  id: string
  name: string
  industry: string | null
  domain: string | null
}

export default function CompanyPicker({
  workspaceId, value, onChange, disabled, placeholder = 'Search or create company…',
}: Props) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(value.name)
  const [open, setOpen] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Sync draft from parent — only when the committed value's identity changes,
  // not on every keystroke (otherwise the user's typing would be clobbered).
  useEffect(() => {
    setDraft(value.name)
  }, [value.id])

  const { data: companies = [] } = useQuery<CompanyRow[]>({
    queryKey: ['companies-typeahead', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return []
      const { data } = await supabase
        .from('companies')
        .select('id, name, industry, domain')
        .eq('workspace_id', workspaceId)
        .order('name', { ascending: true })
      return (data ?? []) as CompanyRow[]
    },
    enabled: !!workspaceId && !disabled,
    staleTime: 30_000,
  })

  const trimmedDraft = draft.trim().toLowerCase()
  const matches = useMemo(
    () => trimmedDraft
      ? companies.filter(c => c.name.toLowerCase().includes(trimmedDraft)).slice(0, 8)
      : companies.slice(0, 8),
    [companies, trimmedDraft],
  )
  const exactMatch = useMemo(
    () => companies.find(c => c.name.toLowerCase() === trimmedDraft),
    [companies, trimmedDraft],
  )
  const showCreateCta = !!draft.trim() && !exactMatch

  // Click-outside handler to close dropdown. Click on a result button still
  // fires onMouseDown before blur, so picks work.
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const pick = (c: CompanyRow) => {
    onChange({ id: c.id, name: c.name })
    setDraft(c.name)
    setOpen(false)
  }

  const clear = () => {
    onChange({ id: null, name: '' })
    setDraft('')
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px 9px 32px',
    background: 'var(--field-bg)', border: '1px solid var(--border-default)',
    borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
    outline: 'none', boxSizing: 'border-box',
  }

  if (disabled) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, opacity: 0.85 }}>
        <Building2 style={{ width: 13, height: 13, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: 'var(--ink-primary)' }}>{value.name || '—'}</span>
      </div>
    )
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)', pointerEvents: 'none' }} />
        <input
          value={draft}
          onChange={e => { setDraft(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={inp}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
        />
        {value.id && draft === value.name && (
          <button onClick={clear}
            aria-label="Clear company"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
            <X style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>

      {value.id && draft !== value.name && (
        <p style={{ fontSize: 11, color: 'var(--warning)', margin: '4px 0 0' }}>
          Linked to <strong>{value.name}</strong>. Pick a result or "Create new" to change.
        </p>
      )}
      {!value.id && draft.trim() && (
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>
          Pick a match or "Create new" to attach a company. Typing alone leaves the contact unlinked.
        </p>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
              background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
              borderRadius: 10, padding: 4, zIndex: 200, maxHeight: 280, overflowY: 'auto',
              boxShadow: 'var(--shadow-md, 0 6px 24px rgba(0,0,0,0.12))',
            }}>
            {matches.length === 0 && !showCreateCta && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-tertiary)' }}>
                {companies.length === 0 ? 'No companies yet. Type a name to create one.' : 'No matches — type a name to search.'}
              </div>
            )}
            {matches.map(c => (
              <button key={c.id} type="button"
                onMouseDown={e => { e.preventDefault(); pick(c) }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'none', border: 'none', borderRadius: 7, cursor: 'pointer', textAlign: 'left', color: 'var(--ink-primary)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Building2 style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{c.name}</p>
                  {(c.industry || c.domain) && (
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)' }}>
                      {[c.industry, c.domain].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </button>
            ))}
            {showCreateCta && (
              <button type="button"
                onMouseDown={e => { e.preventDefault(); setOpen(false); setShowCreateModal(true) }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'none', border: 'none', borderRadius: 7, cursor: 'pointer', textAlign: 'left', color: 'var(--accent-text)', borderTop: matches.length > 0 ? '1px solid var(--border-subtle)' : 'none', marginTop: matches.length > 0 ? 4 : 0, paddingTop: matches.length > 0 ? 10 : 8 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Plus style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Create new company</p>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)' }}>"{draft.trim()}"</p>
                </div>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <NewCompanyModal
            initialName={draft.trim()}
            onClose={() => setShowCreateModal(false)}
            onCreated={(id, name) => {
              // Companies typeahead query needs to see the new row before the
              // next render — invalidate before commit so the parent's
              // immediate handleSave (or follow-up reads) don't miss it.
              queryClient.invalidateQueries({ queryKey: ['companies-typeahead', workspaceId] })
              queryClient.invalidateQueries({ queryKey: ['companies'] })
              onChange({ id, name })
              setDraft(name)
              setShowCreateModal(false)
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
